/** What a character in the amount is, which is what decides how it moves. */
export type AmountGlyphKind = 'prefix' | 'placeholder' | 'digit' | 'separator'

export interface AmountGlyph {
  /** Identity across renders: what mounts is what animates in. */
  key: string
  char: string
  kind: AmountGlyphKind
}

const GROUP_SEPARATOR = ','

/**
 * The amount split into the characters that animate independently.
 *
 * Keys are what mount and unmount, so they decide what animates. Digits (and
 * the decimal point) are keyed by their position among themselves, skipping
 * group separators — so `555 → 5,555` keeps every digit's identity and only
 * mounts the typed digit. A separator's key carries its slot AND the integer
 * digit count, so growing the integer part remounts every comma: commas
 * dissolve and reappear at their new slots rather than sliding sideways through
 * the digits (they sit at grid positions, not in the digit stream). The
 * fraction is not in that count — typing cents leaves the commas where they are.
 */
export function amountGlyphs(value: string, prefix: string): AmountGlyph[] {
  const glyphs: AmountGlyph[] = []
  if (prefix) {
    glyphs.push({ key: 'prefix', char: prefix, kind: 'prefix' })
  }

  // Its own identity, not the digit zero, so typing over it unmounts it rather
  // than reading as a `0` that turned into a `1`.
  if (value === '') {
    glyphs.push({ key: 'placeholder', char: '0', kind: 'placeholder' })
    return glyphs
  }

  const chars = [...value]
  const integerDigits = integerDigitsRightOf(chars, -1)
  let digitIndex = 0
  for (const [index, char] of chars.entries()) {
    if (char === GROUP_SEPARATOR) {
      glyphs.push({
        key: `separator-${integerDigitsRightOf(chars, index)}-${integerDigits}`,
        char,
        kind: 'separator',
      })
      continue
    }
    // A leading integer zero IS the placeholder: `''`, `0` and the `0` of
    // `0.5` share one glyph, so none of those transitions moves it — only a
    // digit typed over it does.
    if (digitIndex === 0 && isLeadingZero(chars)) {
      glyphs.push({ key: 'placeholder', char, kind: 'placeholder' })
      digitIndex += 1
      continue
    }
    glyphs.push({ key: `digit-${digitIndex}`, char, kind: 'digit' })
    digitIndex += 1
  }
  return glyphs
}

/** `0` or `0.…` — the zero that stands in for "nothing yet". */
function isLeadingZero(chars: string[]): boolean {
  return chars[0] === '0' && (chars.length === 1 || chars[1] === '.')
}

/** Digits between `index` and the end of the integer part. */
function integerDigitsRightOf(chars: string[], index: number): number {
  let count = 0
  for (let i = index + 1; i < chars.length; i += 1) {
    if (chars[i] === '.') {
      break
    }
    if (chars[i] !== GROUP_SEPARATOR) {
      count += 1
    }
  }
  return count
}
